/**
 * PRV-008: Modal de Gesti&oacute;n de Solicitud de Edición
 *
 * Permite al supervisor comercial aprobar o rechazar solicitudes de Edición.
 * Al aprobar, la factibilidad regresa a estado PENDIENTE para Edición.
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Grid,
  Divider,
  Chip
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useFetchPutSolicitudEdicion } from '@/shared/actions/erp/solicitud_edicion_factibilidad';
import { ISolicitudEdicionFactibilidad } from '@/shared/interfaces/erp/factibilidad_corporativo';

interface ModalGestionSolicitudEdicionProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  solicitud: ISolicitudEdicionFactibilidad | null;
  accionInicial?: 'ver' | 'aprobar' | 'rechazar';
}

export function ModalGestionSolicitudEdicion({
  open,
  setOpen,
  solicitud,
  accionInicial: _accionInicial = 'ver'
}: ModalGestionSolicitudEdicionProps) {
  const [observaciones, setObservaciones] = useState('');

  const { mutate: gestionarSolicitud, isPending } = useFetchPutSolicitudEdicion({
    enableSuccessAlert: true,
    enableRedirect: false,
    inSiteRefresh: true
  });

  // Limpiar observaciones cuando se cierra el modal
  useEffect(() => {
    if (!open) {
      setObservaciones('');
    }
  }, [open]);

  const handleGestionar = (tipoAccion: 'APROBADO' | 'RECHAZADO') => {
    if (!solicitud) return;

    gestionarSolicitud({
      id: solicitud.id_solicitud_edicion_factibilidad,
      data: {
        estado_solicitud: tipoAccion,
        observaciones_gestion: observaciones.trim() || undefined
      }
    }, {
      onSuccess: () => {
        handleClose();
      }
    });
  };

  const handleClose = () => {
    if (isPending) return;
    setObservaciones('');
    setOpen(false);
  };

  if (!solicitud) return null;

  const factibilidad = solicitud.factibilidad_corporativa_data;
  const prospecto = factibilidad?.prospecto_corporativo_data;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Gesti&oacute;n de Solicitud de Edición
          </Typography>
          <Chip
            label={solicitud.estado_solicitud}
            color="warning"
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* Informaci&oacute;n de la Solicitud */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Informaci&oacute;n de la Solicitud
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                C&oacute;digo Solicitud
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {solicitud.codigo}
              </Typography>
            </Grid>

            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Factibilidad
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {factibilidad?.codigo || 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Fecha Solicitud
              </Typography>
              <Typography variant="body1">
                {new Date(solicitud.fecha_creacion).toLocaleString('es-EC')}
              </Typography>
            </Grid>

            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Solicitante
              </Typography>
              <Typography variant="body1">
                {solicitud.user_create_data
                  ? `${solicitud.user_create_data.first_name} ${solicitud.user_create_data.last_name}`
                  : 'N/A'}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Informaci&oacute;n del Cliente */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Informaci&oacute;n del Cliente
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Cliente
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {prospecto?.nombre_comercial || prospecto?.razon_social || 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Identificaci&oacute;n
              </Typography>
              <Typography variant="body1">
                {prospecto?.identificacion || 'N/A'}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Motivo de la Solicitud */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Motivo de la Solicitud
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            {solicitud.motivo}
          </Alert>

          {/* Advertencia sobre la Acci&oacute;n */}
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Importante:</strong> Si aprueba esta solicitud:
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>La factibilidad regresar&aacute; al estado <strong>PENDIENTE</strong></li>
              <li>Se resetear&aacute;n los flags de pre-aprobaci&oacute;n e inventario</li>
              <li>El asesor podr&aacute; modificar equipos, materiales y componentes</li>
              <li>La factibilidad deber&aacute; pasar nuevamente por el flujo de aprobaci&oacute;n</li>
            </ul>
          </Alert>

          {/* Observaciones */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Observaciones de la gesti&oacute;n (opcional)"
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder="Agregue observaciones sobre su decisi&oacute;n..."
            helperText="Estas observaciones quedar&aacute;n registradas en la trazabilidad"
            disabled={isPending}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={handleClose}
          disabled={isPending}
          color="inherit"
        >
          Cancelar
        </Button>

        <Box>
          <Button
            onClick={() => handleGestionar('RECHAZADO')}
            variant="outlined"
            color="error"
            disabled={isPending}
            sx={{ mr: 1 }}
          >
            {isPending ? 'Procesando...' : 'Rechazar'}
          </Button>

          <Button
            onClick={() => handleGestionar('APROBADO')}
            variant="contained"
            color="success"
            disabled={isPending}
          >
            {isPending ? 'Procesando...' : 'Aprobar'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default ModalGestionSolicitudEdicion;
